import { z } from 'zod';
import type { Device } from './events.ts';

/**
 * The persona's traits. Every trait changes something: a sentence of the visitor's prompt, a rule of
 * the engine (what they notice, how long things take, when they give up), or the brief of the visit. A
 * level trait is a range: each visit draws its own value from it with the test's seed, so visits differ
 * and stay reproducible.
 *
 * The configurator shows only the traits a customer can set with confidence (see `TRAIT_GROUPS`). The
 * others (setting, set time, decision style, budget, stages, cookies, navigation, reviews) stay in the
 * schema: the ready-made personas use them, and personas saved earlier keep their values. New personas
 * get their defaults, or values derived from the visible traits (`sensesFor`, `cookiesFor`).
 */

export const VISIT_STAGES = ['browsing', 'comparing', 'ready'] as const;
export type VisitStage = (typeof VISIT_STAGES)[number];

export const visitStageLabel: Record<VisitStage, string> = {
  browsing: 'Just looking',
  comparing: 'Comparing options',
  ready: 'Ready to act',
};

export const DEAL_BREAKERS = [
  'signup_before_price',
  'surprise_costs',
  'asks_phone',
  'forced_account',
  'no_reviews',
  'no_contact',
  'popups',
  'slow_pages',
  'no_free_option',
  'no_language',
] as const;
export type DealBreaker = (typeof DEAL_BREAKERS)[number];

/** How the deal-breaker reads in the configurator and in the visitor's prompt ("You leave as soon as …"). */
export const dealBreakerText: Record<DealBreaker, string> = {
  signup_before_price: 'having to sign up before seeing the price',
  surprise_costs: 'costs that show up only at the end (shipping, fees)',
  asks_phone: 'being asked for a phone number',
  forced_account: 'being forced to create an account to buy',
  no_reviews: 'finding no reviews at all',
  no_contact: 'finding no way to reach a real person',
  popups: 'pop-ups that keep getting in the way',
  slow_pages: 'pages that take long to load',
  no_free_option: 'no free option or trial',
  no_language: 'no version in your language',
};

export const CONNECTIONS = ['fast', 'average', 'slow'] as const;
export type Connection = (typeof CONNECTIONS)[number];
export const DECISION_STYLES = ['impulsive', 'compares', 'researches'] as const;
export type DecisionStyle = (typeof DECISION_STYLES)[number];
export const COOKIE_HABITS = ['accept', 'reject', 'close', 'ignore'] as const;
export type CookieHabit = (typeof COOKIE_HABITS)[number];
export const NAVIGATION_HABITS = ['mixed', 'menu', 'search'] as const;
export type NavigationHabit = (typeof NAVIGATION_HABITS)[number];

const level = z.number().int().min(1).max(5);
/** A 1-5 trait as the range a visit draws from. */
export const levelRangeSchema = z
  .object({ min: level, max: level })
  .refine((r) => r.min <= r.max, { message: 'The lowest value must not be above the highest' });
export type LevelRange = z.infer<typeof levelRangeSchema>;

const minutesRangeSchema = z
  .object({ min: z.number().min(0.5).max(60), max: z.number().min(0.5).max(60) })
  .refine((r) => r.min <= r.max, { message: 'The shortest time must not be above the longest' });

const range = (min: number, max = min): LevelRange => ({ min, max });

export const personaTraitsSchema = z.object({
  // Context
  connection: z.enum(CONNECTIONS).default('fast'),
  /** Where they are and what else is going on ("on the bus", "between two meetings"). */
  setting: z.string().trim().max(200).default(''),
  /** Time they have for the visit, in minutes; null: from patience and attention. */
  timeMinutes: minutesRangeSchema.nullable().default(null),
  // Digital skills
  /** 1 = struggles with technology, 5 = power user. */
  familiarity: levelRangeSchema.default(range(3)),
  /** 1 = looks only at headlines and prices, 5 = reads everything. */
  reading: levelRangeSchema.default(range(2, 3)),
  /** 1 = needs large text (zooms in), 5 = sees well. */
  vision: levelRangeSchema.default(range(4, 5)),
  /** 1 = often taps the wrong thing, 5 = precise. */
  precision: levelRangeSchema.default(range(4, 5)),
  /** Command of the site's language: 1 = barely understands it, 5 = native. */
  fluency: levelRangeSchema.default(range(5)),
  // Psychology
  /** 1 = leaves at the first obstacle, 5 = very persistent. */
  patience: levelRangeSchema.default(range(3)),
  /** 1 = trusts easily, 5 = needs a lot of proof. */
  trust: levelRangeSchema.default(range(3)),
  decision: z.enum(DECISION_STYLES).default('compares'),
  /** Most they will spend, in euros; null: no set budget. */
  budgetEur: z.number().min(0).max(1_000_000).nullable().default(null),
  /** Where they can be with their decision; each visit is in one of these. */
  stages: z.array(z.enum(VISIT_STAGES)).min(1).max(3).default(['browsing', 'comparing', 'ready']),
  // Reasons to leave
  dealBreakers: z.array(z.enum(DEAL_BREAKERS)).max(DEAL_BREAKERS.length).default([]),
  customDealBreakers: z.array(z.string().trim().min(1).max(200)).max(5).default([]),
  // Habits
  cookies: z.enum(COOKIE_HABITS).default('accept'),
  navigation: z.enum(NAVIGATION_HABITS).default('mixed'),
  readsReviews: z.boolean().default(false),
});
export type PersonaTraits = z.infer<typeof personaTraitsSchema>;
export type PersonaTraitsInput = z.input<typeof personaTraitsSchema>;

/**
 * The deal-breakers the configurator offers. "No version in your language" is left out: the persona's
 * language and how well they understand the site's say it already.
 */
export const CONFIGURABLE_DEAL_BREAKERS: readonly DealBreaker[] = DEAL_BREAKERS.filter((d) => d !== 'no_language');

/** The values one visit drew from the persona's traits. */
export interface SampledTraits {
  familiarity: number;
  reading: number;
  vision: number;
  precision: number;
  fluency: number;
  patience: number;
  trust: number;
  decision: DecisionStyle;
  budgetEur: number | null;
  connection: Connection;
  setting: string;
  cookies: CookieHabit;
  navigation: NavigationHabit;
  readsReviews: boolean;
  dealBreakers: string[];
}

interface LegacyPersona {
  age: number | null;
  device: Device;
  techSavvy: number;
  patience: number;
  traits?: PersonaTraits | null;
}

/** Sight and precision of taps that go with an age and a device, when nobody set them. */
export function sensesFor(age: number | null, device: Device, techSavvy: number): { vision: LevelRange; precision: LevelRange } {
  const a = age ?? 40;
  return {
    vision: a >= 70 ? range(1, 2) : a >= 60 ? range(2, 3) : range(4, 5),
    precision: a >= 70 || (device === 'mobile' && techSavvy <= 1) ? range(2, 3) : range(3, 5),
  };
}

/** How a visitor handles cookie banners, from how much they need to trust a site. */
export function cookiesFor(trust: LevelRange): CookieHabit {
  return Math.round((trust.min + trust.max) / 2) >= 4 ? 'reject' : 'accept';
}

/**
 * The traits of a persona: its own, or for personas made before the configurator, traits derived from
 * tech savviness, patience, age and device.
 */
export function resolveTraits(p: LegacyPersona): PersonaTraits {
  if (p.traits) return p.traits;
  const base = personaTraitsSchema.parse({});
  return {
    ...base,
    familiarity: range(p.techSavvy),
    patience: range(p.patience),
    reading: p.techSavvy >= 4 ? range(2, 4) : range(1, 3),
    ...sensesFor(p.age, p.device, p.techSavvy),
  };
}

/** techSavvy and patience of the persona, kept in step with its traits (the middle of the range). */
export function legacyLevels(traits: PersonaTraits): { techSavvy: number; patience: number } {
  const mid = (r: LevelRange) => Math.round((r.min + r.max) / 2);
  return { techSavvy: mid(traits.familiarity), patience: mid(traits.patience) };
}

function draw(rng: () => number, r: LevelRange): number {
  return r.min + Math.floor(rng() * (r.max - r.min + 1)) % (r.max - r.min + 1);
}

/** Draws one visit's values. `rng` is the visit's seeded generator: same seed, same values. */
export function sampleTraits(traits: PersonaTraits, rng: () => number): SampledTraits {
  return {
    familiarity: draw(rng, traits.familiarity),
    reading: draw(rng, traits.reading),
    vision: draw(rng, traits.vision),
    precision: draw(rng, traits.precision),
    fluency: draw(rng, traits.fluency),
    patience: draw(rng, traits.patience),
    trust: draw(rng, traits.trust),
    decision: traits.decision,
    budgetEur: traits.budgetEur,
    connection: traits.connection,
    setting: traits.setting,
    cookies: traits.cookies,
    navigation: traits.navigation,
    readsReviews: traits.readsReviews,
    dealBreakers: [...traits.dealBreakers.map((d) => dealBreakerText[d]), ...traits.customDealBreakers],
  };
}

/** Minutes a visitor has by patience (1-5), when the persona does not say. */
const MINUTES_BY_PATIENCE: [number, number][] = [
  [1, 2],
  [1.5, 3],
  [2.5, 5],
  [4, 8],
  [6, 12],
];

/**
 * Seconds the visitor has for this visit. Attention and a mission stretch or shorten it; an inspection
 * has no clock (null).
 */
export function sampleTimeBudget(
  traits: PersonaTraits,
  patience: number,
  attention: 'low' | 'medium' | 'high',
  rng: () => number,
  opts: { mission: boolean; inspection: boolean },
): number | null {
  if (opts.inspection) return null;
  const [lo, hi] = traits.timeMinutes ? [traits.timeMinutes.min, traits.timeMinutes.max] : MINUTES_BY_PATIENCE[Math.max(0, Math.min(4, patience - 1))]!;
  let minutes = lo + rng() * (hi - lo);
  if (!traits.timeMinutes) minutes *= attention === 'low' ? 0.7 : attention === 'high' ? 1.3 : 1;
  if (opts.mission) minutes *= 1.5;
  return Math.round(minutes * 60);
}

/** What the engine does with the drawn values. The runner uses it, the configurator preview shows it. */
export interface EngineEffects {
  /** Most interactive elements the visitor notices on one screen; null: all of them (inspection). */
  visibleElements: number | null;
  /** Icons without text are seen as "icon" instead of their hidden label. */
  iconsAsShapes: boolean;
  /** Browser zoom: 1.25 or 1.5 for poor sight. */
  zoom: number;
  /** Chance that a tap on a small target hits something next to it. */
  mistapChance: number;
  /** Chance of a typo in text typed into a field (never in emails or passwords). */
  typoChance: number;
  connection: Connection;
  /** Words read per second, and the share of the visible words actually read. */
  wordsPerSecond: number;
  readShare: number;
  /** Seconds to find and press something, to type one character, to scroll. */
  clickSeconds: number;
  charSeconds: number;
  scrollSeconds: number;
  /** Frustration points after which the visitor may give up; null: never (inspection). */
  frustrationThreshold: number | null;
}

const pickBy = <T>(values: T[], level: number): T => values[Math.max(0, Math.min(values.length - 1, level - 1))]!;

export function engineEffects(
  t: SampledTraits,
  opts: { device: Device; attention: 'low' | 'medium' | 'high'; inspection: boolean },
): EngineEffects {
  const touch = opts.device !== 'desktop';
  const attention = opts.attention === 'low' ? 0.7 : opts.attention === 'high' ? 1.25 : 1;
  const visible = Math.max(6, Math.round(pickBy([10, 15, 24, 40, 80], t.familiarity) * attention * (t.reading <= 1 ? 0.85 : 1)));
  return {
    visibleElements: opts.inspection ? null : visible,
    iconsAsShapes: !opts.inspection && t.familiarity <= 2,
    zoom: pickBy([1.5, 1.25, 1, 1, 1], t.vision),
    mistapChance: opts.inspection ? 0 : pickBy([0.3, 0.15, 0.05, 0, 0], t.precision) * (touch ? 1 : 0.5),
    typoChance: opts.inspection ? 0 : Math.min(0.5, pickBy([0.35, 0.15, 0, 0, 0], t.precision) + (t.familiarity <= 1 ? 0.1 : 0)),
    connection: t.connection,
    wordsPerSecond: 3.5 * (t.vision <= 2 ? 0.7 : 1) * pickBy([0.4, 0.6, 0.8, 1, 1], t.fluency),
    readShare: Math.min(1, pickBy([0.1, 0.25, 0.45, 0.7, 1], t.reading) * (opts.attention === 'low' ? 0.7 : 1)),
    clickSeconds: pickBy([4, 3, 2, 1.5, 1], t.familiarity),
    charSeconds: pickBy([0.7, 0.5, 0.3, 0.22, 0.18], t.familiarity) * (touch ? 1.3 : 1),
    scrollSeconds: 1.5,
    frustrationThreshold: opts.inspection ? null : pickBy([4, 6, 8, 11, 14], t.patience),
  };
}

const FAMILIARITY = [
  'You use your phone or computer for messages and news, little else. Icons without words confuse you and you look for big buttons with clear words. If you cannot find something after a couple of tries, you think it is not there.',
  'You get by online, but menus with many options and unusual layouts slow you down: you stick to what looks familiar.',
  'You are used to the web: menus, search boxes and online checkouts are normal to you.',
  'You are at ease online: you use search and filters and move quickly.',
  'You are a power user: search, filters, several tabs. You notice at once what is missing.',
];
const READING = [
  'You only look at headlines, pictures, buttons and prices. You never read paragraphs.',
  'You skim: headlines first, a sentence or two when something catches your eye.',
  'You read short paragraphs when they look relevant and skip the long ones.',
  'You read most of a page before deciding.',
  'You read everything carefully, small print included.',
];
const VISION = ['You see poorly: small text is hard for you, so you zoom in.', 'Small text tires you; you prefer large, clear text.'];
const PRECISION = [
  'Your taps are imprecise: with small buttons close together you often hit the wrong one.',
  'Small, crowded buttons sometimes make you tap the wrong one.',
];
const FLUENCY = [
  'You barely understand the language of this site: you look for a version in your language and go by pictures, prices and the few words you know.',
  'You understand the language of this site only in part: long or technical sentences lose you.',
  'You understand the language of this site, but it is not your first: jargon slows you down.',
];
const PATIENCE = [
  'You have very little patience: a page gets a few seconds, and after two dead ends you leave.',
  'You are impatient: if something does not work quickly, you look elsewhere.',
  'You give a site a fair chance, but not forever.',
  'You are patient: you try a few ways before giving up.',
  'You are very persistent: you keep trying until you get there or it is clearly impossible.',
];
const TRUST = [
  'You trust a site easily if it looks decent.',
  'A professional look is enough for you to trust a site.',
  'Before paying or leaving your details you want some reassurance: reviews, real contact details, a clear price.',
  'You are wary: you look for reviews, who is behind the company and how to get your money back.',
  'You are very suspicious of sites you do not know: without strong proof (reviews, company details, guarantees) you will not leave your data or your money.',
];
const DECISION: Record<DecisionStyle, string> = {
  impulsive: 'You decide on impulse: if something looks good, you go for it.',
  compares: 'You compare the options before choosing.',
  researches: 'You research thoroughly before deciding, and often decide later.',
};
const COOKIES: Record<CookieHabit, string> = {
  accept: 'Cookie banners: you press accept to get rid of them.',
  reject: 'Cookie banners: you look for "reject" or "only necessary".',
  close: 'Cookie banners: you close them with the X when there is one.',
  ignore: 'You ignore cookie banners as long as you can still use the page.',
};
const NAVIGATION: Record<NavigationHabit, string | null> = {
  mixed: null,
  menu: 'You find your way through the menu.',
  search: 'When there is a search box, you go straight for it.',
};
const CONNECTION: Record<Connection, string | null> = {
  fast: null,
  average: 'Your connection is so-so right now: pages take a moment.',
  slow: 'Your connection is slow right now.',
};

/** The sentences of the visitor's prompt that come from the traits, in the second person. */
export function traitSentences(t: SampledTraits): { howYouUseTheWeb: string[]; whatMakesYouTrust: string[]; whatMakesYouLeave: string[]; habits: string[] } {
  const web = [pickBy(FAMILIARITY, t.familiarity), pickBy(READING, t.reading)];
  if (t.vision <= 2) web.push(pickBy(VISION, t.vision));
  if (t.precision <= 2) web.push(pickBy(PRECISION, t.precision));
  if (t.fluency <= 3) web.push(pickBy(FLUENCY, t.fluency));
  const connection = CONNECTION[t.connection];
  if (connection) web.push(connection);
  web.push(pickBy(PATIENCE, t.patience));
  const trust = [pickBy(TRUST, t.trust), DECISION[t.decision]];
  if (t.readsReviews) trust.push('You always look for reviews before deciding.');
  if (t.budgetEur !== null) trust.push(`You do not want to spend more than €${t.budgetEur}.`);
  const leave = t.dealBreakers.length > 0 ? [`You leave as soon as you run into: ${t.dealBreakers.join('; ')}.`] : [];
  const habits = [COOKIES[t.cookies], NAVIGATION[t.navigation]].filter((s): s is string => !!s);
  return { howYouUseTheWeb: web, whatMakesYouTrust: trust, whatMakesYouLeave: leave, habits };
}

export const stageSentence: Record<VisitStage, string> = {
  browsing: 'You are just looking around for now: you do not plan to buy or sign up today.',
  comparing: 'You are comparing this with other options before choosing.',
  ready: 'You are ready to act today if it convinces you.',
};

/**
 * The configurator's groups, in order, and where each is used: ordinary visits, inspections, or both.
 * `more` holds the traits most personas leave at their default (collapsed).
 */
export const TRAIT_GROUPS = [
  { key: 'who', label: 'Who they are', modes: ['human', 'check'] },
  { key: 'browse', label: 'How they browse', modes: ['human'] },
  { key: 'leave', label: 'What makes them leave', modes: ['human', 'check'] },
  { key: 'goal', label: 'Why they come', modes: ['human', 'check'] },
  { key: 'more', label: 'More traits', modes: ['human', 'check'] },
] as const;
